import {
  LoadingOutlined,
} from "@ant-design/icons";
import {
  Empty,
  Pagination,
  Spin,
} from "antd";
import isUndefined from "lodash/isUndefined";
import React, { PureComponent, ReactNode, isValidElement } from "react";
import { CurrencyEnum } from "utils/enums/CurrencyEnum";
import { colorFormat } from "utils/format/ColorFormat";
import { formatCurrency } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import ActionButtonComponent from "../actionButtonComponent/ActionButtonComponent";
import ModalConfirmationComponent from "../modalConfirmation/ModalConfirmationComponent";
import { ActionFormat } from "utils/format/ActionFormat";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { BasePermissionCapability } from "app/based/baseApiModel/basePermissionMenu/BasePermissionCapability";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import {
  MobileCardBlockConfig,
  MobileCardSlotType,
  TableComponentModel,
} from "./model/TableComponentModel";
import { ColumnsType, ColumnType } from "antd/es/table";

interface MobileCardListProps<T> {
  data: TableComponentModel<T>["data"];
  page?: number;
  pageSize?: number;
  total?: number;
}

export default class MobileCardList<T extends Object> extends PureComponent<
  MobileCardListProps<T>
> {
  private isClickProcessing = false;

  private get cachedCapability() {
    return productCache?.getCache(
      CacheKeyEnum.capabilities,
    ) as Array<BasePermissionCapability>;
  }

  /** Mirror of TableComponent.getValue */
  private getValue = (obj: T, path: any): any =>
    Array.isArray(path)
      ? path.reduce((acc: any, key: string) => acc?.[key], obj)
      : (obj as any)?.[path];

  /** Render a column cell — handles ReactNode | RenderedCell<T>, falls back to raw value or dash */
  private renderCell = (
    col: ColumnType<T>,
    record: T,
    recordIndex: number,
  ): ReactNode => {
    const raw = this.getValue(record, col.dataIndex);
    if (col.render) {
      const rendered = col.render(raw, record, recordIndex);
      if (
        rendered !== null &&
        typeof rendered === "object" &&
        !isValidElement(rendered) &&
        "children" in (rendered as object)
      ) {
        return (rendered as { children?: ReactNode }).children;
      }
      return rendered as any;
    }
    return raw != null && raw !== "" ? String(raw) : "—";
  };

  /** Render mobile top summary card (Total, Success, Error, Other + Revenue) */
  private renderSummaryCard = (total?: number): ReactNode => {
    const { dataSource, summaryInfo, customSummary, totalTitle, summaryComponent } =
      this.props.data;
    const safeTotal = Number.isFinite(total) ? (total as number) : 0;

    // Do not render top summary card if dataSource is empty or total is 0
    if (!dataSource || dataSource.length === 0 || safeTotal === 0) {
      return null;
    }

    if (summaryComponent) {
      return summaryComponent();
    }

    const hasSummaryInfo =
      !!summaryInfo && Object.keys(summaryInfo).length > 0;
    const hasCustomSummary = !!customSummary && customSummary.length > 0;

    if (!hasSummaryInfo && !hasCustomSummary) {
      return null;
    }

    const hasRevenue =
      hasSummaryInfo &&
      (summaryInfo?.revenue?.USD !== undefined ||
        summaryInfo?.revenue?.VND !== undefined);

    return (
      <div className="bg-white rounded-xl border border-slate-200/80 p-3 mb-3 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
        {/* Top metrics grid */}
        {hasCustomSummary ? (
          <div
            className="grid divide-x divide-slate-100 text-center"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                customSummary.length + 1,
                4,
              )}, minmax(0, 1fr))`,
            }}
          >
            <div className="flex flex-col items-center justify-center px-1">
              <span className="text-[17px] font-bold leading-tight text-blue-600">
                {new Intl.NumberFormat().format(safeTotal)}
              </span>
              <span className="text-[11px] text-slate-500 font-normal mt-0.5 truncate max-w-full">
                {totalTitle || strings.GlobalLabels.Total}
              </span>
            </div>
            {customSummary.map((item, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center justify-center px-1 min-w-0"
              >
                <span
                  className="text-[17px] font-bold leading-tight truncate max-w-full"
                  style={item.color ? { color: item.color } : undefined}
                >
                  {typeof item.number === "number"
                    ? new Intl.NumberFormat().format(item.number)
                    : item.number}
                </span>
                <span className="text-[11px] text-slate-500 font-normal mt-0.5 truncate max-w-full">
                  {item.title}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-4 divide-x divide-slate-100 text-center">
            {/* Total */}
            <div className="flex flex-col items-center justify-center px-1">
              <span className="text-[17px] font-bold leading-tight text-blue-600">
                {new Intl.NumberFormat().format(safeTotal)}
              </span>
              <span className="text-[11px] text-slate-500 font-normal mt-0.5 truncate max-w-full">
                {totalTitle || strings.GlobalLabels.Total}
              </span>
            </div>

            {/* Success */}
            <div className="flex flex-col items-center justify-center px-1">
              <span className="text-[17px] font-bold leading-tight text-emerald-600">
                {new Intl.NumberFormat().format(summaryInfo?.finished ?? 0)}
              </span>
              <span className="text-[11px] text-slate-500 font-normal mt-0.5 truncate max-w-full">
                {strings.GlobalTotalSummary.Success}
              </span>
            </div>

            {/* Error / Failed */}
            <div className="flex flex-col items-center justify-center px-1">
              <span className="text-[17px] font-bold leading-tight text-red-500">
                {new Intl.NumberFormat().format(summaryInfo?.error ?? 0)}
              </span>
              <span className="text-[11px] text-slate-500 font-normal mt-0.5 truncate max-w-full">
                {strings.GlobalTotalSummary.Error}
              </span>
            </div>

            {/* Other */}
            <div className="flex flex-col items-center justify-center px-1">
              <span className="text-[17px] font-bold leading-tight text-slate-700">
                {new Intl.NumberFormat().format(summaryInfo?.other ?? 0)}
              </span>
              <span className="text-[11px] text-slate-500 font-normal mt-0.5 truncate max-w-full">
                {strings.GlobalTotalSummary.Other}
              </span>
            </div>
          </div>
        )}

        {/* Revenue section */}
        {hasRevenue && (
          <div className="border-t border-slate-100 pt-2.5 mt-2.5 grid grid-cols-2 gap-3">
            <div className="flex flex-col">
              <span className="text-[11px] text-slate-500 font-normal leading-tight">
                {`${strings.GlobalTotalSummary.Revenue} (USD)`}
              </span>
              <span className="text-[13.5px] font-bold text-emerald-600 mt-0.5 leading-tight">
                {formatCurrency(
                  summaryInfo?.revenue?.USD ?? 0,
                  false,
                  true,
                  CurrencyEnum.$,
                )}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-[11px] text-slate-500 font-normal leading-tight">
                {`${strings.GlobalTotalSummary.Revenue} (VND)`}
              </span>
              <span className="text-[13.5px] font-bold text-emerald-600 mt-0.5 leading-tight">
                {formatCurrency(
                  summaryInfo?.revenue?.VND ?? 0,
                  false,
                  true,
                  CurrencyEnum.VND,
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  private resolveSlotContent = (
    slot: MobileCardSlotType<T> | undefined,
    record: T,
    recordIndex: number,
    columns: ColumnsType<T>,
    stt?: number,
  ): ReactNode => {
    if (isUndefined(slot) || slot === null || slot === "") return null;

    if (typeof slot === "function") {
      return slot(record, recordIndex, stt);
    }

    if (typeof slot === "string") {
      const matchedCol = (columns as any[]).find(
        (c) => c?.key === slot || c?.dataIndex === slot,
      );

      if (matchedCol) {
        return this.renderCell(matchedCol, record, recordIndex);
      }

      const rawVal = (record as any)?.[slot];
      if (!isUndefined(rawVal) && rawVal !== null && rawVal !== "") {
        return rawVal;
      }
      return null;
    }

    return slot;
  };

  private renderCard = (record: T, recordIndex: number): ReactNode => {
    const { data } = this.props;
    const {
      columns,
      rowKey,
      onDetailClick,
      onDeleteClick,
      customActionChidren,
      mobileCard,
      needActionButton = () => true,
      needRenderFeature = true,
      deleteCapabilityKey = CapabilityEnum.delete,
      detailCapabilityKey = CapabilityEnum.detail,
    } = data;

    const isHasDetailKey = this.cachedCapability?.some((item) =>
      item?.key?.includes(detailCapabilityKey),
    );
    const isHasDeleteKey = this.cachedCapability?.some((item) =>
      item?.key?.includes(deleteCapabilityKey),
    );

    const canShowDetail =
      (!isUndefined(onDetailClick) && !needRenderFeature) ||
      (isHasDetailKey &&
        !isUndefined(onDetailClick) &&
        needRenderFeature === true);

    const canShowDelete =
      (!isUndefined(onDeleteClick) && !needRenderFeature) ||
      (isHasDeleteKey &&
        !isUndefined(onDeleteClick) &&
        needRenderFeature === true);

    const detailButton = canShowDetail
      ? ActionFormat.renderActions({
        keyword: detailCapabilityKey,
        needFollowingFeature: needRenderFeature,
        onClick: async () => await onDetailClick!(record),
        renderType: "detail",
        label: strings.GlobalLabels.Detail,
      })
      : undefined;

    const deleteButton = canShowDelete
      ? ActionFormat.renderActions({
        keyword: deleteCapabilityKey,
        renderType: "delete",
        needFollowingFeature: needRenderFeature,
        color: colorFormat?.Red_Dark,
        label: strings.GlobalLabels.Deleted,
        onClick: async () =>
          ModalConfirmationComponent({
            data: {
              title: strings.GlobalMessage.DeleteConfirmation,
              isDanger: true,
              onOk: async () => await onDeleteClick!(record),
            },
          }),
      })
      : undefined;

    const rawButtonProps = [detailButton, deleteButton].filter(
      (c): c is NonNullable<typeof c> => !isUndefined(c),
    );

    const customChildren =
      !isUndefined(customActionChidren) && customActionChidren(record);
    const hasCustomActions = !!customChildren;

    const totalActionsCount =
      rawButtonProps.length + (hasCustomActions ? 1 : 0);

    // Hide detail button if it is the only action (< 2 actions)
    const buttonProps = rawButtonProps.filter((btn) => {
      if (btn === detailButton && totalActionsCount < 2) {
        return false;
      }
      return true;
    });

    const hasVisibleActions = buttonProps.length > 0 || hasCustomActions;

    // Resolve card key
    let cardKey: string | number = recordIndex;
    if (typeof rowKey === "function") {
      cardKey = (rowKey as (r: T) => string)(record);
    } else if (typeof rowKey === "string") {
      cardKey = (record as any)[rowKey] ?? recordIndex;
    }

    const { page, pageSize } = this.props;
    const safePage = Number.isFinite(page) ? (page as number) : 0;
    const safePageSize =
      Number.isFinite(pageSize) && (pageSize as number) > 0
        ? (pageSize as number)
        : 10;
    const stt = safePage * safePageSize + recordIndex + 1;

    // Filter valid non-system columns
    const validCols = (columns as ColumnType<T>[]).filter((c) => {
      if (!c) return false;
      if (
        c.title === strings.GlobalLabels.STT ||
        c.title === strings.GlobalLabels.Actions
      )
        return false;
      return true;
    });

    // Resolve mobileCard configuration
    const resolvedConfig =
      typeof mobileCard === "function"
        ? (mobileCard as any)(record, recordIndex, stt)
        : mobileCard;

    // Direct ReactNode overwrite for entire card
    if (isValidElement(resolvedConfig)) {
      return (
        <div
          key={cardKey}
          className={`rounded-xl bg-white border border-slate-200/80 p-3 mb-2.5 shadow-sm ${canShowDetail ? "cursor-pointer hover:border-blue-300" : ""
            }`}
          onClick={
            canShowDetail ? () => onDetailClick!(record) : undefined
          }
        >
          {resolvedConfig}
        </div>
      );
    }

    let mainConfig:
      | MobileCardSlotType<T>[]
      | ((record: T, index: number, stt?: number) => ReactNode)
      | undefined;
    let avatarNode: ReactNode | undefined;
    let subsConfig:
      | (ReactNode | ((record: T, index: number, stt?: number) => ReactNode))[]
      | ((record: T, index: number, stt?: number) => (ReactNode | null | undefined)[])
      | undefined;
    let detailConfig:
      | (string | ColumnType<T>)[]
      | ((record: T, index: number, stt?: number) => ReactNode)
      | undefined;

    if (Array.isArray(resolvedConfig)) {
      mainConfig = resolvedConfig;
    } else if (resolvedConfig && typeof resolvedConfig === "object") {
      const blockConfig = resolvedConfig as MobileCardBlockConfig<T>;
      mainConfig = blockConfig.main;
      avatarNode =
        typeof blockConfig.avatar === "function"
          ? blockConfig.avatar(record, recordIndex, stt)
          : blockConfig.avatar;
      subsConfig = blockConfig.subs;
      detailConfig = blockConfig.detail;
    }

    // Default main slots fallback
    const activeSlots: MobileCardSlotType<T>[] =
      Array.isArray(mainConfig) && mainConfig.length > 0
        ? mainConfig
        : [
          validCols[0]?.key ?? validCols[0]?.dataIndex,
          validCols[1]?.key ?? validCols[1]?.dataIndex,
          validCols[2]?.key ?? validCols[2]?.dataIndex,
          validCols[3]?.key ?? validCols[3]?.dataIndex,
        ]
          .filter(
            (s): s is NonNullable<typeof s> => !isUndefined(s) && s !== null,
          )
          .map((s) => (typeof s === "string" ? s : String(s)));

    const usedMainKeys = new Set<string>();
    activeSlots.forEach((s) => {
      if (typeof s === "string") usedMainKeys.add(s);
    });

    // ── Block 1: Main Slots ──
    const leftSlots = activeSlots.filter((_, idx) => idx % 2 === 0);
    const rightSlots = activeSlots.filter((_, idx) => idx % 2 === 1);

    // ── Block 2: Subs List ──
    let renderedSubs: (ReactNode | null | undefined)[] = [];
    if (typeof subsConfig === "function") {
      renderedSubs = subsConfig(record, recordIndex, stt);
    } else if (Array.isArray(subsConfig)) {
      renderedSubs = subsConfig.map((item) =>
        typeof item === "function" ? item(record, recordIndex, stt) : item,
      );
    }
    const validSubs = (renderedSubs || []).filter(
      (node) =>
        node !== null && node !== undefined && node !== false && node !== "",
    );

    // ── Block 3: Detail Columns (2-column layout: label top, value bottom) ──
    const isCustomDetailFunc = typeof detailConfig === "function";
    let detailCols: ColumnType<T>[] = [];

    if (!isCustomDetailFunc) {
      if (Array.isArray(detailConfig) && detailConfig.length > 0) {
        detailCols = detailConfig
          .map((item) => {
            if (typeof item === "string") {
              return (columns as ColumnType<T>[]).find(
                (c) => c?.key === item || c?.dataIndex === item,
              );
            }
            return item;
          })
          .filter((c): c is ColumnType<T> => !isUndefined(c));
      } else {
        // Auto fallback: all remaining columns not rendered in main
        detailCols = validCols.filter((c) => {
          if (
            (c.key && usedMainKeys.has(c.key as string)) ||
            (c.dataIndex && usedMainKeys.has(c.dataIndex as string))
          ) {
            return false;
          }
          return true;
        });
      }
    }

    const detailLeft = detailCols.filter((_, idx) => idx % 2 === 0);
    const detailRight = detailCols.filter((_, idx) => idx % 2 === 1);
    const maxDetailRows = Math.max(detailLeft.length, detailRight.length);
    const hasDetailContent = isCustomDetailFunc || maxDetailRows > 0;

    return (
      <div
        key={cardKey}
        className={`rounded-xl border transition-all duration-150 p-3 mb-2.5 bg-white border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.03)] ${canShowDetail
          ? "cursor-pointer hover:border-blue-300 hover:shadow-md active:bg-slate-50/70"
          : ""
          }`}
        onClick={
          canShowDetail && needActionButton(record)
            ? () => {
                if (this.isClickProcessing) return;
                this.isClickProcessing = true;
                Promise.resolve(onDetailClick!(record)).finally(() => {
                  this.isClickProcessing = false;
                });
              }
            : undefined
        }
      >
        {/* ── Block 1: Main Header ── */}
        <div>
          {typeof mainConfig === "function" ? (
            mainConfig(record, recordIndex, stt)
          ) : (
            <div className="flex items-start justify-between gap-x-2.5">
              {/* Left section: Avatar + Left slots */}
              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                {avatarNode ? (
                  <div className="shrink-0 mt-0.5">{avatarNode}</div>
                ) : (
                  <span className="inline-flex items-center justify-center text-[11px] font-bold rounded px-1.5 py-0.5 min-w-[20px] h-[20px] shrink-0 mt-0.5 text-slate-600 bg-slate-100 border border-slate-200/80">
                    #{stt}
                  </span>
                )}

                <div className="flex flex-col gap-y-0.5 min-w-0 flex-1">
                  {leftSlots.map((slot, index) => {
                    const content = this.resolveSlotContent(
                      slot,
                      record,
                      recordIndex,
                      columns,
                      stt,
                    );
                    if (
                      content === null ||
                      content === undefined ||
                      content === ""
                    )
                      return null;
                    return (
                      <div
                        key={index}
                        className={`leading-[1.4] break-words max-w-full [&_p]:m-0 [&_p]:p-0 ${index === 0
                          ? "text-[13px] font-bold text-slate-900"
                          : "text-[12px] text-slate-500"
                          }`}
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right section: Right slots (Price, Status, etc.) */}
              <div className="flex flex-col items-end gap-y-0.5 text-right min-w-0 shrink-0">
                {rightSlots.map((slot, index) => {
                  const content = this.resolveSlotContent(
                    slot,
                    record,
                    recordIndex,
                    columns,
                    stt,
                  );
                  if (
                    content === null ||
                    content === undefined ||
                    content === ""
                  )
                    return null;
                  return (
                    <div
                      key={index}
                      className={`leading-[1.4] break-words text-right max-w-full [&_p]:m-0 [&_p]:p-0 ${index === 0
                        ? "text-[13px] font-bold text-emerald-600"
                        : "text-[12px] font-medium"
                        }`}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Block 2: Subs List ── */}
        {validSubs.length > 0 && (
          <div className="flex flex-col gap-y-1.5 pt-2 mt-2 border-t border-slate-100">
            {validSubs.map((subNode, subIdx) => (
              <div key={subIdx}>{subNode}</div>
            ))}
          </div>
        )}

        {/* ── Block 3: Detail Block (Always visible: 2-column layout) ── */}
        {hasDetailContent && (
          <div className="pt-2.5 mt-2.5 border-t border-dashed border-slate-200">
            {typeof detailConfig === "function" ? (
              detailConfig(record, recordIndex, stt)
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {Array.from({ length: maxDetailRows }, (_, rowIdx) => {
                  const leftCol = detailLeft[rowIdx];
                  const rightCol = detailRight[rowIdx];

                  return (
                    <React.Fragment key={rowIdx}>
                      {/* Left cell: label on top, value below */}
                      <div className="flex flex-col min-w-0">
                        {leftCol ? (
                          <>
                            <span className="text-[11px] text-[#8c8c8c] leading-[1.3] truncate max-w-full">
                              {leftCol.title as ReactNode}
                            </span>
                            <div className="text-[12px] font-medium text-slate-800 leading-[1.4] break-words max-w-full mt-0.5 [&_p]:m-0 [&_p]:p-0">
                              {this.renderCell(leftCol, record, recordIndex)}
                            </div>
                          </>
                        ) : null}
                      </div>

                      {/* Right cell: label on top, value below (aligned right) */}
                      <div className="flex flex-col min-w-0 items-end text-right">
                        {rightCol ? (
                          <>
                            <span className="text-[11px] text-[#8c8c8c] leading-[1.3] truncate max-w-full text-right">
                              {rightCol.title as ReactNode}
                            </span>
                            <div className="text-[12px] font-medium text-slate-800 leading-[1.4] break-words max-w-full text-right mt-0.5 [&_p]:m-0 [&_p]:p-0">
                              {this.renderCell(rightCol, record, recordIndex)}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Action footer (if has delete, custom actions, or 2+ actions) ── */}
        {hasVisibleActions && needActionButton(record) && (
          <div
            className="flex justify-center items-center gap-2 pt-2 mt-2 border-t border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionButtonComponent
              data={{
                children: customChildren,
                buttonProps: buttonProps,
              }}
            />
          </div>
        )}
      </div>
    );
  };

  render(): ReactNode {
    const { data, page, pageSize, total } = this.props;
    const { dataSource, loading, loadingTitle, handleTableChange } = data;

    const safePage = Number.isFinite(page) ? (page as number) : 0;
    const safePageSize =
      Number.isFinite(pageSize) && (pageSize as number) > 0
        ? (pageSize as number)
        : 10;
    const safeTotal = Number.isFinite(total) ? (total as number) : 0;

    const hasSummary =
      (!!data.summaryInfo && Object.keys(data.summaryInfo).length > 0) ||
      (!!data.customSummary && data.customSummary.length > 0) ||
      !!data.summaryComponent;

    return (
      <div className="mt-1 w-full">
        <Spin
          spinning={loading}
          indicator={
            <LoadingOutlined className="text-blue-600 font-semibold" />
          }
          tip={
            <span className="font-semibold text-blue-600">{loadingTitle}</span>
          }
        >
          {/* Top Summary Card (Total, Status metrics, Revenue) */}
          {this.renderSummaryCard(safeTotal)}

          {/* Card list */}
          <div className="flex flex-col">
            {!dataSource || dataSource.length === 0 ? (
              <div className="py-6">
                <Empty
                  description={strings.GlobalLabels.NoInfo}
                  styles={{ image: { fontSize: "12px" } }}
                />
              </div>
            ) : (
              dataSource.map((record, idx) => this.renderCard(record, idx))
            )}
          </div>

          {/* Pagination */}
          <div
            className={`flex items-center ${hasSummary ? "justify-end" : "justify-between"
              } flex-wrap gap-2 mt-3 px-0`}
          >
            {!hasSummary && (
              <div className="flex-auto min-w-0">
                <div className="flex items-center text-xs min-w-0 font-medium">
                  <span className="text-black">
                    {data.totalTitle || strings.GlobalLabels.Total}:
                  </span>
                  <span className="text-[13px] font-bold text-blue-600 ml-1">
                    {new Intl.NumberFormat().format(safeTotal)}
                  </span>
                </div>
              </div>
            )}
            <Pagination
              current={safePage + 1}
              pageSize={safePageSize}
              total={safeTotal}
              simple={true}
              showSizeChanger={true}
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={(currentPage, size) =>
                handleTableChange?.({
                  current: currentPage,
                  pageSize: size,
                })
              }
              className="flex-none"
            />
          </div>
        </Spin>
      </div>
    );
  }
}
